import { v4 as uuidv4 } from 'uuid';

const getPublicPdfUrl = (fileName) => `${process.env.PUBLIC_URL || ''}/locales/${fileName}`;

const TNC_REVIEW_LOCAL_PDF_URLS = {
  en: getPublicPdfUrl('terms-and-conditions_EN.pdf'),
  zh_CN: getPublicPdfUrl('terms-and-conditions_SC.pdf'),
  zh_HK: getPublicPdfUrl('terms-and-conditions_TC.pdf'),
};

export const TNC_REVIEW_PDF_URLS = {
  en: 'EN_TERMS_AND_CONDITIONS',
  zh_CN: 'SC_TERMS_AND_CONDITIONS',
  zh_HK: 'TC_TERMS_AND_CONDITIONS',
};

const preloadCache = new Map();

const getTncReviewLangByDocumentName = (documentName) => Object.keys(TNC_REVIEW_PDF_URLS)
  .find((lang) => TNC_REVIEW_PDF_URLS[lang] === documentName) || 'en';

const decodeBase64ToArrayBuffer = (base64Value) => {
  if (!base64Value || typeof globalThis.atob !== 'function') {
    return null;
  }

  const normalizedBase64 = base64Value.includes(',')
    ? base64Value.split(',').pop()
    : base64Value;
  const binaryString = globalThis.atob(normalizedBase64);
  const bytes = new Uint8Array(binaryString.length);

  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.codePointAt(index);
  }

  return bytes.buffer;
};

const fetchLocalTncReviewPdf = async (documentName) => {
  const lang = getTncReviewLangByDocumentName(documentName);
  const localPdfUrl = TNC_REVIEW_LOCAL_PDF_URLS[lang] || TNC_REVIEW_LOCAL_PDF_URLS.en;
  const response = await globalThis.fetch(localPdfUrl);

  if (!response.ok) {
    throw new Error(`Unable to load local PDF: ${response.status}`);
  }

  return response.arrayBuffer();
};

export const downloadTncReviewPdfData = async (documentName) => {
  if (!documentName || typeof globalThis.fetch !== 'function') {
    return null;
  }

  if (process.env.MOCK === 'on' || !process.env.downloadDocumentsApi) {
    return fetchLocalTncReviewPdf(documentName);
  }

  try {
    const response = await globalThis.fetch(process.env.downloadDocumentsApi, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-HSBC-Chnl-CountryCode': 'HK',
        'X-HSBC-Chnl-Group-Member': 'HBAP',
        'X-HSBC-Global-Channel-Id': 'WEB',
        'X-HSBC-Client-Id': 'dummy',
        'X-HSBC-Source-System-Id': '12648576',
        'X-HSBC-Request-Correlation-Id': uuidv4(),
        'Accept-Language': 'en_GB',
        'X-HSBC-Src-UserAgent': globalThis.navigator?.userAgent || '',
      },
      body: JSON.stringify({
        data: {
          documentName,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Unable to download PDF: ${response.status}`);
    }

    const payload = await response.json();
    const pdfArrayBuffer = decodeBase64ToArrayBuffer(payload?.data?.documentContentValue);

    if (!pdfArrayBuffer) {
      throw new Error('Downloaded PDF content is empty');
    }

    return pdfArrayBuffer;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('Falling back to local terms and conditions PDF:', error);
    }

    return fetchLocalTncReviewPdf(documentName);
  }
};

export const getPreferredTncReviewLang = () => {
  const navigatorLang = globalThis.navigator?.language?.replace('-', '_') || 'en';

  if (navigatorLang.startsWith('zh_CN')) {
    return 'zh_CN';
  }

  if (navigatorLang.startsWith('zh_HK') || navigatorLang.startsWith('zh_TW')) {
    return 'zh_HK';
  }

  return 'en';
};

export const preloadTncReviewPdf = (lang) => {
  const documentName = TNC_REVIEW_PDF_URLS[lang] || TNC_REVIEW_PDF_URLS.en;

  if (process.env.NODE_ENV === 'test' || !documentName || typeof globalThis.fetch !== 'function') {
    return null;
  }

  if (!preloadCache.has(documentName)) {
    const preloadPromise = Promise.resolve()
      .then(() => downloadTncReviewPdfData(documentName))
      .catch((error) => {
        preloadCache.delete(documentName);

        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('Failed to preload terms and conditions PDF:', error);
        }

        return null;
      });

    preloadCache.set(documentName, preloadPromise);
  }

  return preloadCache.get(documentName);
};

export const preloadPreferredTncReviewPdf = () => preloadTncReviewPdf(getPreferredTncReviewLang());

export const getPreloadedTncPdfData = async (documentName) => {
  const preloadedPdf = preloadCache.get(documentName);

  if (!preloadedPdf) {
    return null;
  }

  const pdfArrayBuffer = await preloadedPdf;

  if (!pdfArrayBuffer) {
    return null;
  }

  preloadCache.delete(documentName);

  return pdfArrayBuffer.slice(0);
};
